/**
 * useAdminAccess — admin-UI gating sourced from RBAC capabilities, with a
 * legacy-role fallback (RBAC Phase 4 wiring).
 *
 * Historically the admin UI gated on the pre-RBAC `user.role === 'admin'`
 * string. That check never migrated onto the capability model, so a FIRM_ADMIN
 * — whose privilege lives in a `UserRoleAssignment`, not the legacy `role`
 * column — was locked out of every admin screen (the route guard bounced them
 * to /v4/records). This hook resolves admin access from the new capabilities:
 *   - `firm.manage`     → the CLIENT firm-administration surface
 *   - `platform.manage` → the INTERNAL / platform surface (GO Lab, Evaluate)
 * while still honoring legacy `role === 'admin'` users so existing accounts are
 * unaffected.
 *
 * Domain boundary: a FIRM_ADMIN (CLIENT super-user) must reach firm
 * administration but NOT the INTERNAL GO Lab / Evaluate tooling — hence the
 * firm-vs-platform split rather than a single boolean. The backend remains the
 * source of truth; this only decides what the UI shows / routes to.
 */
import { useAuth } from '../contexts/AuthContext';
import { isAdmin as isLegacyAdmin } from '../utils/rbac';
import { useAccess, Capability } from './useAccess';

export interface AdminAccess {
  /** CLIENT firm administration (users, teams, roles, features, audit, settings). */
  isFirmAdmin: boolean;
  /** INTERNAL / platform administration (GO Lab, Evaluate, cross-org tooling). */
  isPlatformAdmin: boolean;
  /** Holds either admin surface — the broad "is this user an admin at all" gate. */
  isAnyAdmin: boolean;
  /** True while capabilities are still loading; guards should wait, not bounce. */
  isLoading: boolean;
}

export function useAdminAccess(): AdminAccess {
  const { user } = useAuth();
  const { can, isLoading } = useAccess();

  // Legacy `role === 'admin'` users predate the capability model and must keep
  // full access, so they satisfy both surfaces.
  const legacy = isLegacyAdmin(user);
  const isFirmAdmin = legacy || can(Capability.FirmManage);
  const isPlatformAdmin = legacy || can(Capability.PlatformManage);

  return {
    isFirmAdmin,
    isPlatformAdmin,
    isAnyAdmin: isFirmAdmin || isPlatformAdmin,
    isLoading,
  };
}
